// Admin → Audit log: admin actions and sign-in activity.
import { useCallback, useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { clock, dayLabel } from '../../lib/format';
import type { AuditLog } from '../../lib/types';
import { useParent } from '../ParentContext';
import { SignInActivity } from '../SignInActivity';

/** Start-of-day / end-of-day epoch ms for a yyyy-mm-dd date input, in the viewer's local time. */
const dayStart = (d: string) => (d ? new Date(`${d}T00:00:00`).getTime() : undefined);
const dayEnd = (d: string) => (d ? new Date(`${d}T23:59:59.999`).getTime() : undefined);
/** Admin → Audit log: what admins did, or everyone's sign-ins (#429). */
export function Audit() {
  const [view, setView] = useState<'actions' | 'sign-ins'>('actions');
  return (
    <div className="stack">
      <div className="segmented audit-views" role="radiogroup" aria-label="Show">
        <button role="radio" aria-checked={view === 'actions'} className={view === 'actions' ? 'active' : ''} onClick={() => setView('actions')}>
          Admin actions
        </button>
        <button role="radio" aria-checked={view === 'sign-ins'} className={view === 'sign-ins' ? 'active' : ''} onClick={() => setView('sign-ins')}>
          Sign-ins
        </button>
      </div>
      {view === 'actions' ? <AdminActions /> : <SignInActivity />}
    </div>
  );
}
function AdminActions() {
  const { toast } = useParent();
  const [data, setData] = useState<AuditLog | null>(null);
  const [admin, setAdmin] = useState('');
  const [action, setAction] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [q, setQ] = useState('');
  const [query, setQuery] = useState(''); // debounced search term actually sent

  // Debounce the free-text search so we don't refetch on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setQuery(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (admin) p.set('admin', admin);
    if (action) p.set('action', action);
    const f = dayStart(from);
    const e = dayEnd(to);
    if (f) p.set('from', String(f));
    if (e) p.set('to', String(e));
    if (query) p.set('q', query);
    api.get<AuditLog>(`/api/admin/audit?${p.toString()}`).then(setData).catch((err) => toast({ text: (err as Error).message, tone: 'alert' }));
  }, [admin, action, from, to, query, toast]);
  useEffect(load, [load]);

  const filtered = !!(admin || action || from || to || query);
  const reset = () => {
    setAdmin('');
    setAction('');
    setFrom('');
    setTo('');
    setQ('');
  };

  return (
    <div className="stack">
      <section className="card stack audit-filters">
        <div className="form-row">
          <label className="grow">
            Who
            <select value={admin} onChange={(e) => setAdmin(e.target.value)}>
              <option value="">Anyone</option>
              {(data?.admins ?? []).map((a) => (
                <option key={a.id} value={a.id}>
                  {a.label}
                </option>
              ))}
            </select>
          </label>
          <label className="grow">
            Action
            <select value={action} onChange={(e) => setAction(e.target.value)}>
              <option value="">Any action</option>
              {(data?.actions ?? []).map((a) => (
                <option key={a.id} value={a.id}>
                  {a.label}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="form-row">
          <label className="grow">
            From
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} max={to || undefined} />
          </label>
          <label className="grow">
            To
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} min={from || undefined} />
          </label>
        </div>
        <label>
          Search
          <input placeholder="Search names, emails or details" value={q} onChange={(e) => setQ(e.target.value)} />
        </label>
        {filtered && (
          <div>
            <button className="btn small" onClick={reset}>
              Clear filters
            </button>
          </div>
        )}
      </section>

      {!data ? (
        <p className="muted">Loading…</p>
      ) : !data.entries.length ? (
        <p className="muted">{filtered ? 'No matching admin actions.' : 'No admin actions yet.'}</p>
      ) : (
        <section className="card">
          <ul className="admin-audit">
            {data.entries.map((r) => (
              <li key={r.id}>
                <div className="audit-title">{r.title ?? r.action}</div>
                {!!r.lines?.length && (
                  <ul className="audit-lines">
                    {r.lines.map((l, i) => (
                      <li key={i}>{l}</li>
                    ))}
                  </ul>
                )}
                <div className="muted small">
                  {r.who ?? r.adminEmail} · {dayLabel(r.ts)} {clock(r.ts)}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
